/**
 * TubesheetDetailsTable Component
 * 
 * Renders specialized tables for tubesheet welding procedures.
 * Provides detailed information specific to tube-to-tubesheet welding applications.
 * 
 * Supports three table types:
 * - JointDetails: Joint specifications including hole ID, tube pitch, groove details
 * - OtherVariables: Additional welding variables like expansion, cleaning methods
 * - PostWeldHeatTreatment: PWHT parameters including temperature, time, rates
 * 
 * Features:
 * - Joint geometry specifications (hole ID, tube pitch, projection)
 * - Groove parameters (depth, angle, width of ligament)
 * - Welding technique variables (oscillation, peening, electrode type)
 * - Heat treatment schedules with loading/unloading temperatures
 * - Process-specific notes and requirements
 */

const TubesheetDetailsTable = ({ pdf, type }) => {
  const pdfData = pdf[0];

  const renderJointDetails = () => (
    <table id="table" className="WPSTable2" style={{ width: "72%", paddingBottom: "0px", bottom: "0px", marginBottom: "0px" }}>
      <tbody>
        <tr style={{ marginBottom: "0px" }}>
          <th style={{ borderTop: "0.5px solid black", width: "15%", borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Joint Type
          </th>
          <td className="table2firstcolumn" style={{ borderTop: "0.5px solid black", padding: "1px 4px", width: "28%" }}>
            {pdfData?.WPSH_JD_JointType}
          </td>
          <th style={{ borderTop: "0.5px solid black", borderRight: "0.5px solid black", borderLeft: "0.5px solid black", width: "20%" }}>
            Hole Id
            <div className="WPSrcorner">mm</div>
          </th>
          <td className="table2firstcolumn" style={{ borderTop: "0.5px solid black", borderRight: "0.5px solid black", width: "35%" }}>
            {pdfData?.WPSH_JD_HoleId}
          </td>
        </tr>
        
        <tr>
          <th style={{ borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Tube Pitch
            <div className="WPSrcorner">mm</div>
          </th>
          <td className="table2firstcolumn" style={{ padding: "1px 4px" }}>
            {pdfData?.WPSH_JD_TubePitch}
          </td>
          <th style={{ borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Groove Depth
            <div className="WPSrcorner">mm</div>
          </th>
          <td className="table2firstcolumn" style={{ borderRight: "0.5px solid black" }}>
            {pdfData?.WPSH_JD_GrooveDepth}
          </td>
        </tr>
        
        <tr style={{ borderBottom: "0" }}>
          <th style={{ borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Tube Projection
            <div className="WPSrcorner">mm</div>
          </th>
          <td className="table2firstcolumn" style={{ padding: "1px 4px" }}>
            {pdfData?.WPSH_JD_TubeProj}
          </td>
          <th style={{ borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Width of the ligament
            <div className="WPSrcorner">mm</div>
          </th>
          <td className="table2firstcolumn" style={{ borderRight: "0.5px solid black" }}>
            {pdfData?.WPSH_JD_WidthLigament}
          </td>
        </tr>
        
        <tr style={{ borderBottom: "0" }}>
          <th style={{ borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Pattern
          </th>
          <td className="table2firstcolumn" style={{ padding: "1px 4px" }}>
            {pdfData?.WPSH_JD_Pattern}
          </td>
          <th style={{ borderRight: "0.5px solid black", borderLeft: "0.5px solid black" }}>
            Groove Angle(°)/Radius(mm)
          </th>
          <td className="table2firstcolumn" style={{ borderRight: "0.5px solid black" }}>
            {pdfData?.WPSH_JD_GrooveAngle}
          </td>
        </tr>
      </tbody>
    </table>
  );

  const renderOtherVariables = () => (
    <table id="table" className="WPSTable9" style={{ position: "relative", top: "0px" }}>
      <tbody>
        {[
          { label: "Expansion", field: "WPSH_Tech_Expansion" },
          { label: "Method of cleaning", field: "WPSH_Tech_MethodofCleaning" },
          { label: "Single /Multi Electrode", field: "WPSH_Tech_SingleMultiElectrode" },
          { label: "Peening", field: "WPSH_Tech_Peening" },
          { label: "Use of Thermal Process", field: "WPSH_Tech_UseOfThermalProcess" },
          { label: "String & Weave", field: "WPSH_Tech_StringWave" },
          { label: "Oscillation", field: "WPSH_Tech_Oscillation" }
        ].map((item, index) => (
          <tr key={item.field} style={index === 0 ? { borderTop: ".1px solid black" } : {}}>
            <th scope="row" style={index === 0 ? { borderTop: ".1px solid black", width: "17%" } : { width: "17%" }}>
              {item.label}
              {item.field === "WPSH_Tech_OrifceCupSize" && <div className="WPSrcorner">mm</div>}
            </th>
            <td className="WPSNoLeft" style={{ borderLeft: "none" }}>
              {pdfData?.[item.field]}
            </td>
          </tr>
        ))}
        
        <tr>
          <th scope="row">
            Orifice Cup size
            <div className="WPSrcorner">mm</div>
          </th>
          <td className="WPSNoLeft" style={{ borderLeft: "none" }}>
            {pdfData?.WPSH_Tech_OrifceCupSize}
          </td>
        </tr>
        
        <tr>
          <th scope="row" style={{ borderBottom: "1px solid black" }}>
            Closed to out chamber
          </th>
          <td className="WPSNoLeft" style={{ borderLeft: "none", borderBottom: "1px solid black" }}>
            {pdfData?.WPSH_Tech_ClosedtooutChamber}
          </td>
        </tr>
      </tbody>
    </table>
  );

  const renderPostWeldHeatTreatment = () => (
    <table id="table" className="WPSTable8" style={{ position: "relative", top: "5px" }}>
      <tbody>
        <tr>
          <th style={{ width: "15%", borderTop: ".1px solid black", borderLeft: ".1px solid black", borderBottom: ".1px solid black" }}>
            Temperature
            <div className="WPSrcorner">°C</div>
          </th>
          <td style={{ width: "26%", borderBottom: "1px solid black", borderTop: "1px solid black", paddingLeft: "3px" }}>
            {pdfData?.WPSH_PWHeatTre_Temperature}
          </td>
          <th style={{ width: "17%", borderTop: "1px solid black", borderBottom: "1px solid black" }}>
            Time
            <div className="WPSrcorner">Minutes</div>
          </th>
          <td style={{ width: "25%", borderBottom: "1px solid black", borderTop: "1px solid black", borderRight: "none", borderLeft: "none", paddingLeft: "3px" }}>
            {pdfData?.WPSH_PWHeatTre_Time}
          </td>
          <th style={{ width: "5%", borderTop: ".1px solid black", borderLeft: ".1px solid black" }} rowSpan="3">
            Type
          </th>
          <td rowSpan="3" style={{ width: "20%", borderTop: "1px solid black", borderRight: "1px solid black", borderBottom: "1px solid black", paddingLeft: "3px" }}>
            {pdfData?.WPSH_PWHeatTre_Type}
          </td>
        </tr>
        
        <tr>
          <th style={{ width: "25%", borderLeft: ".1px solid black" }}>
            Heating Rate
            <div className="WPSrcorner">°C/hr</div>
          </th>
          <td style={{ width: "15%", borderRight: "none", paddingLeft: "3px", borderBottom: "1px solid black" }}>
            {pdfData?.WPSH_PWHeatTre_Heating_Rate}
          </td>
          <th style={{ width: "25%" }}>
            Cooling Rate
            <div className="WPSrcorner">°C/hr</div>
          </th>
          <td style={{ width: "15%", paddingLeft: "3px", borderBottom: "1px solid black" }}>
            {pdfData?.WPSH_PWHeatTre_Cooling_Rate}
          </td>
        </tr>
        
        <tr>
          <th style={{ width: "25%", borderLeft: ".1px solid black" }}>
            Loading Temp.
            <div className="WPSrcorner">°C</div>
          </th>
          <td style={{ width: "15%", borderRight: "none", paddingLeft: "3px", borderBottom: "1px solid black" }}>
            {pdfData?.WPSH_PWHeatTre_LoadingTemp}
          </td>
          <th style={{ width: "25%" }}>
            Unloading Temp.
            <div className="WPSrcorner">°C/hr</div>
          </th>
          <td style={{ width: "15%", paddingLeft: "3px", borderBottom: "1px solid black" }}>
            {pdfData?.WPSH_PWHeatTre_UnloadingTemp}
          </td>
        </tr>
        
        <tr>
          <th style={{ width: "25%", borderLeft: ".1px solid black" }}>
            Notes
          </th>
          <td colSpan={5} style={{ width: "45%", borderRight: "1px solid black", paddingLeft: "3px", borderBottom: "1px solid black" }}>
            {pdfData?.WPSH_PWHeatTre_PWHTNotes}
          </td>
        </tr>
      </tbody>
    </table>
  );

  switch (type) {
    case "JointDetails":
      return renderJointDetails();
    case "OtherVariables":
      return renderOtherVariables();
    case "PostWeldHeatTreatment":
      return renderPostWeldHeatTreatment();
    default:
      return null;
  }
};

export default TubesheetDetailsTable;